import { useState } from 'react';
import {
  type AiSermonItem,
  AI_SERMON_PREVIEW_LENGTH,
  AI_SERMON_TYPE_LABELS,
  AI_SERMON_TYPE_ORDER,
  deleteAiSermonItem,
  formatAiSermonDate,
} from '../../common/aiSermonApi';
import { fetchSongsByThemes, type PraiseRecommendSong } from '../../../aiservice/common/fetchSongsByThemes';

type Props = {
  items: AiSermonItem[];
  userAccount: string;
  onDeleteGroup: (ids: number[]) => void;
};

export default function AiSermonGroupCard({ items, userAccount, onDeleteGroup }: Props) {
  const orderedTypes = AI_SERMON_TYPE_ORDER.filter((type) => items.some((item) => item.type === type));
  const [activeType, setActiveType] = useState(orderedTypes[0]);
  const [expanded, setExpanded] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState(false);
  const [praiseSongs, setPraiseSongs] = useState<PraiseRecommendSong[] | null>(null);
  const [praiseLoading, setPraiseLoading] = useState(false);

  const activeItem = items.find((item) => item.type === activeType);
  const { date, time } = formatAiSermonDate(items[0].createdAt);

  const handleTabChange = async (type: AiSermonItem['type']) => {
    setActiveType(type);
    setExpanded(false);

    if (type === 'praiseThemes' && praiseSongs === null) {
      const item = items.find((i) => i.type === 'praiseThemes');
      if (!item) return;
      setPraiseLoading(true);
      try {
        const themes: string[] = JSON.parse(item.resultContent);
        const songs = await fetchSongsByThemes(themes);
        setPraiseSongs(songs);
      } catch (error) {
        console.error('찬양 목록 조회 오류:', error);
        setPraiseSongs([]);
      } finally {
        setPraiseLoading(false);
      }
    }
  };

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyFeedback(true);
      setTimeout(() => setCopyFeedback(false), 1500);
    } catch {
      alert('복사에 실패했습니다.');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('이 생성 결과를 모두 삭제하시겠습니까?')) return;
    const ids = items.map((item) => item.id);
    const results = await Promise.all(ids.map((id) => deleteAiSermonItem(id, userAccount)));
    if (results.every(Boolean)) {
      onDeleteGroup(ids);
    } else {
      alert('삭제에 실패했습니다.');
    }
  };

  const praiseThemes: string[] = activeType === 'praiseThemes' && activeItem
    ? JSON.parse(activeItem.resultContent)
    : [];

  return (
    <article className="mypage-scrap-item">
      <div className="mypage-row-between">
        <div className="mypage-tag-row">
          {orderedTypes.map((type) => (
            <button
              type="button"
              key={type}
              className={`mypage-filter-chip${activeType === type ? ' is-active' : ''}`}
              onClick={() => handleTabChange(type)}
            >
              {AI_SERMON_TYPE_LABELS[type]}
            </button>
          ))}
        </div>
        <span className="mypage-meta">
          <span>{date}</span>
          {time ? <span>{time}</span> : null}
        </span>
      </div>

      {activeType === 'praiseThemes' ? (
        <>
          <div className="praise-recommend-themes" style={{ marginTop: 10 }}>
            {praiseThemes.map((theme) => (
              <span className="praise-recommend-theme-chip" key={theme}>{theme}</span>
            ))}
          </div>
          {praiseLoading ? (
            <p style={{ marginTop: 10 }}>찬양 목록을 불러오는 중입니다...</p>
          ) : praiseSongs && praiseSongs.length > 0 ? (
            <div className="praise-recommend-song-list" style={{ marginTop: 10 }}>
              {praiseSongs.map((song) => (
                <div className="praise-recommend-song-card" key={song.id}>
                  <div className="praise-recommend-song-info">
                    <span className="praise-recommend-song-title">{song.title}</span>
                    <span className="praise-recommend-song-meta">{song.stateSort} · {song.keySort} · {song.tempoSort}</span>
                  </div>
                  <button
                    type="button"
                    className="mypage-btn mypage-btn--outline"
                    onClick={() => window.open(`/aiservice/worship/detail?id=${song.id}`, '_blank')}
                  >
                    자세히보기
                  </button>
                </div>
              ))}
            </div>
          ) : praiseSongs && praiseSongs.length === 0 ? (
            <p style={{ marginTop: 10 }}>매칭된 주제에 해당하는 찬양을 찾지 못했습니다.</p>
          ) : null}
        </>
      ) : activeItem ? (
        <>
          <p style={{ whiteSpace: 'pre-wrap', marginTop: 10, lineHeight: 1.8 }}>
            {expanded || activeItem.resultContent.length <= AI_SERMON_PREVIEW_LENGTH
              ? activeItem.resultContent
              : `${activeItem.resultContent.slice(0, AI_SERMON_PREVIEW_LENGTH)}...`}
          </p>
          <div className="mypage-row-between" style={{ marginTop: 12 }}>
            {activeItem.resultContent.length > AI_SERMON_PREVIEW_LENGTH ? (
              <button
                type="button"
                className="mypage-btn mypage-btn--outline"
                onClick={() => setExpanded((prev) => !prev)}
              >
                {expanded ? '접기' : '전체보기'}
              </button>
            ) : <span />}
            <button
              type="button"
              className="mypage-btn mypage-btn--outline"
              onClick={() => handleCopy(activeItem.resultContent)}
            >
              {copyFeedback ? '복사됨' : '복사'}
            </button>
          </div>
        </>
      ) : null}

      <div className="mypage-row-between" style={{ marginTop: 12 }}>
        <span />
        <button type="button" className="mypage-btn mypage-btn--outline" onClick={handleDelete}>
          삭제
        </button>
      </div>
    </article>
  );
}
